/**
 * Landing Page
 * Identifierar organisation och redirectar till rätt inloggningsmetod
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api/client';
import { useTranslation } from '@/store/languageStore';

interface OrganizationInfo {
  organizationId: string;
  authMethod: 'PASSWORD' | 'SAML2' | 'OIDC';
  organizationName: string;
}

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    identifyOrganization();
  }, []);

  const identifyOrganization = async () => {
    try {
      setLoading(true);
      setError(null);

      // Skicka med referer header om det finns
      const response = await api.get<{ success: boolean; data: OrganizationInfo }>('/auth/landing', {
        headers: {
          // Referer skickas automatiskt av webbläsaren
        },
      });

      if (response.data.success && response.data.data) {
        const { organizationId, authMethod } = response.data.data;

        if (authMethod === 'SAML2') {
          // Automatisk redirect till SAML initiate
          window.location.href = `/api/auth/saml/${organizationId}/initiate`;
        } else if (authMethod === 'OIDC') {
          // Redirect till SSO-sidan så användaren kan välja OIDC (t.ex. Entra ID)
          navigate(`/auth/sso?org=${encodeURIComponent(organizationId)}`, { replace: true });
        } else {
          // Redirect till vanlig login
          navigate('/login');
        }
      } else {
        setError('Kunde inte identifiera organisation');
        setLoading(false);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kunde inte identifiera organisation');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-textSecondary">{t('auth.identifying') || 'Identifierar organisation...'}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold text-center mb-4 text-gray-800">
            {t('auth.identificationError') || 'Identifiering misslyckades'}
          </h2>
          <p className="text-textSecondary mb-6 text-center">{error}</p>
          <div className="space-y-3">
            <button
              onClick={identifyOrganization}
              className="w-full bg-blue-500 text-white font-bold py-2 px-4 rounded-md hover:bg-blue-600 transition duration-200"
            >
              {t('common.tryAgain') || 'Försök igen'}
            </button>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-gray-500 text-white font-bold py-2 px-4 rounded-md hover:bg-gray-600 transition duration-200"
            >
              {t('auth.goToLogin') || 'Gå till inloggning'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null; // Redirectar automatiskt
};

export default Landing;

